import { NormalizedResponse } from '@ptl/xhr';

/** @description Data is stored encoded, decrypt to ProspectBYFInfoParsed */
export interface AdditionalInfoEncoded {
  additionalInfo: string;
}
export interface AdditionalInfoParsed {
  sepCircumstanceData: {
    effectiveDate;
    eventId;
    lifeEventDate;
    lifeEventName;
    supportingDocumentation;
    dcn;
  };
}

export interface ProspectPlanData {
  planId; // product.productId,
  planArea; // : product.planArea,
  groupNumber; // : product.groupNoValue,
  region; // : product.region
}

/** This field historically used to house random data needed to be stored on the prospect object when returning data from backend */
export interface ProspectBYFInfo {
  additionalInfo: AdditionalInfoEncoded;
  comparePlans?: ProspectPlanData[]; // Compare plans (CHMHCR), when you are actively comparing and want to save progress
  selectedPlan?: ProspectPlanData; // View plans, (CHMHCR), when you click a plan to add to cart;
  // Plan Details (CHMHCR), when you click to see the details and save
}

/** This is used to drive the button displays on the application-status component page */
export interface ProspectApplicationStatus {
  id: number; // 6;
  description?: string; // 'New Customer'
  timestamp?: number;  // comes back for new customer, and newCustomerPostEligibility
  effectiveDate?: string;
}

/** only 11, 6, and 8 seem to map to the values listed in the teamsite file - the rest have variations of "Enrollment Complete..." */
/** Link-Sharing flows - user clicks emailed link from producer */
// Triggered when flow is committed to - hitting enroll button, for example
const START_EMAILED_APPLICATION: ProspectApplicationStatus = { id: 1, description: 'Link Sharing Customer' };
// Triggered when flow is complete - just after application submitted successfully
const UPDATE_EMAILED_APPLICATION: ProspectApplicationStatus = { id: 2, description: 'Link Sharing Customer' };
const NEW_CUSTOMER: ProspectApplicationStatus = { id: 6, description: 'New Customer' };
const EXCEPTION_ON_ELIGIBILITY_APPLICATION: ProspectApplicationStatus = { id: 7, description: 'Exception on Eligibility Application' };
const USER_INITIATED_RETURN: ProspectApplicationStatus = { id: 8, description: 'User Initiated Return' };
const COMPLETED_ELIGIBILITY_APPLICATION: ProspectApplicationStatus = { id: 9, description: 'Completed Eligibility Application' };
const SAVED_QUOTES: ProspectApplicationStatus = { id: 10, description: 'Saved Quotes' };
const SAVED_QUOTES_SHOP_DIRECTLY_HIGHMARK: ProspectApplicationStatus = { id: 10, description: 'Shop Directly With Highmark' };
const ENROLLMENT_APPLICATION_SUBMISSION_FAILED: ProspectApplicationStatus = { id: 11, description: 'Enrollment Application Submission Failed' };
const SUBMITTED_WITHOUT_PAYMENT: ProspectApplicationStatus = { id: 12, description: 'Submitted without Payment' };
const SUBMITTED_WITH_PAYMENT: ProspectApplicationStatus = { id: 13, description: 'Submitted with Payment' };
const SUBMITTED_WITHOUT_PAYMENT_DEPRECATED: ProspectApplicationStatus = { id: 14, description: 'Submitted without Payment' }; // bill me later
const EXPIRED: ProspectApplicationStatus = { id: 15, description: 'Expired' };
const TRANSFER_CONSUMER_TO_FFM: ProspectApplicationStatus = { id: 16, description: 'Transfer Consumer to FFM' };
const ARCHIVED: ProspectApplicationStatus = { id: 17, description: 'Archived' };
const DELETED: ProspectApplicationStatus = { id: 18, description: 'Deleted' };
export const PROSPECT_APPLICATION_STATUSES = {
  START_EMAILED_APPLICATION,
  UPDATE_EMAILED_APPLICATION,
  NEW_CUSTOMER,
  EXCEPTION_ON_ELIGIBILITY_APPLICATION,
  USER_INITIATED_RETURN,
  COMPLETED_ELIGIBILITY_APPLICATION,
  SAVED_QUOTES,
  SAVED_QUOTES_SHOP_DIRECTLY_HIGHMARK,
  ENROLLMENT_APPLICATION_SUBMISSION_FAILED,
  SUBMITTED_WITHOUT_PAYMENT,
  SUBMITTED_WITH_PAYMENT,
  SUBMITTED_WITHOUT_PAYMENT_DEPRECATED,
  EXPIRED,
  TRANSFER_CONSUMER_TO_FFM,
  ARCHIVED,
  DELETED
};

export interface GeneralAgencyAdmin {
  prspAgtLookUpId: string; // personData.prspAgtLookUpId,
  agent: {
    npn: string; // agentNpn,
    agencyId: string; // agentAgencyId
  };
  loginAdminEntitlement: boolean /** General Agency Admin Entitlement (producer) */;
}

export interface Prospect {
  prospectId?: string; // 'PROS599320' - not present during creation

  firstName: string; // 'WYFIRSTNAME'
  lastName: string; // 'WYLASTNAME'
  consumerFFMId?: string; // '3845739847'
  dob: number; // 631083600000
  address1: string; // '123 FAKE STREET'
  address2?: string;
  county?: string;
  city: string; // 'FAKE TOWN'
  state: string; // 'WY'
  zipCode: string; // '82001'
  email: string; // 'asdf@asdf.com'
  effectiveDate?: number; // 1662045254977; - not present during creation

  status?: ProspectApplicationStatus; // not present during creation
  agent?: {
    npn: unknown;
    ffmId: unknown;
    firstName: string;
    lastName: string;
    agencyId: string;
  };

  shoppingInfo: {
    brand: string; // 'WYBCBS
    county?: string; // 'LARAMIE' - not present during creation
    region?: string; // 'WY' - not present during creation
    regionId?: string; // '37' - not present during creation
    zipCode: string; // '82001'
  };

  appdata?: string; //  During enroll submit, this value is populated when submitting the application ( and it used to fetch ratingInfo data if not previously entered - perhaps during an emailed confirmation-page flow?)
  emailCount?: number; // 0;

  additionalInfo?: ProspectBYFInfo;

  applicationType?: 'ONEX' | 'OFFEX';
}

export interface ProspectResponsePayload {
  prospects: Prospect[];
}

export interface ProspectResponse
  extends NormalizedResponse<ProspectResponsePayload> {}

/** from compare-plans.js(CHMHCR), saved quotes flow  */
interface CreateProspectRequestInterface {
  payload: {
    prospect: Prospect;
  };
}
export class CreateProspectRequest implements CreateProspectRequestInterface{
  payload: { prospect: Prospect; };
  constructor(prospect: Prospect) {
    this.payload = { prospect };
  }
}

/** Update - used on consumer-application-status */
export class UpdateProspectStatusRequest {
  public payload: {
    prospect: {
      prospectId: string;
      status: {
        id: number;
        description?: string;
      },
      agent?: {
        npn: string;
        agencyId: string;
      }
    }
  };
  constructor(prospect: Prospect, status: ProspectApplicationStatus, agent?: { npn: string; agencyId: string}) {
    this.payload = {
      prospect: {
        prospectId: prospect.prospectId,
        status: {
          id: status.id,
          description: status.description
        }
      }
    };
    if (agent) {
      this.payload.prospect.agent = agent;
    }
  }

  getAsStringifiedPayload() {
    return JSON.stringify(this);
  }
}

const newCustomer: ProspectResponse = {
  baseMessage: {
    uuid: 'A4EEA8CCF2C94656A140B066099339EB',
    service: 'getProspects',
    serviceOperation: 'GET',
    replier: 'CMCRST',
    repliedTS: 1662045255027,
    returnCode: 200,
    returnCodeDescription: 'OK'
  },
  payload: {
    prospects: [
      {
        prospectId: 'PROS599320',
        firstName: 'WYFIRSTNAME',
        lastName: 'WYLASTNAME',
        dob: 631083600000,
        address1: '123 FAKE STREET',
        city: 'FAKE TOWN',
        state: 'WY',
        zipCode: '82001',
        email: 'asdf@asdf.com',
        effectiveDate: 1662045254977,
        status: {
          id: 6,
          description: 'New Customer',
          timestamp: 1661973488524
        },
        shoppingInfo: {
          zipCode: '82001',
          county: 'LARAMIE',
          regionId: '37',
          region: 'WY',
          brand: 'WYBCBS'
        },
        appdata: '',
        emailCount: 0
      }
    ]
  }
};

const newCustomerAfterCompletingCMSEligibility: ProspectResponse = {
  baseMessage: {
    uuid: '6F0A4B5C73CA4859BF55154274360712',
    service: 'getProspects',
    serviceOperation: 'GET',
    replier: 'CMCRST',
    repliedTS: 1662141340288,
    returnCode: 200,
    returnCodeDescription: 'OK'
  },
  payload: {
    prospects: [
      {
        prospectId: 'PROS599320',
        firstName: 'WYFIRSTNAME',
        lastName: 'WYLASTNAME',
        dob: 631083600000,
        address1: '123 FAKE STREET',
        city: 'FAKE TOWN',
        state: 'WY',
        zipCode: '82001',
        email: 'asdf@asdf.com',
        consumerFFMId: '18715235',
        effectiveDate: 1662141340209,
        status: {
          id: 9,
          description: 'Completed Eligibility Application',
          timestamp: 1662136898830
        },
        shoppingInfo: {
          zipCode: '82001',
          county: 'LARAMIE',
          regionId: '37',
          region: 'WY',
          brand: 'WYBCBS'
        },
        appdata: '',
        emailCount: 0
      }
    ]
  }
};
